import {
  Avatar,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/AuthProvider';
import { useLogout } from '@/api/queries/useAuth';

export const UserMenu = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const logout = useLogout();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleClose();
    navigate('/perfil');
  };

  const handleLogout = () => {
    handleClose();
    logout();
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getUserDisplayName = () => {
    if (!user) return '';
    return user.nombre || user.email;
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        size='small'
        sx={{ ml: 2 }}
        aria-controls={anchorEl ? 'user-menu' : undefined}
        aria-haspopup='true'
        aria-expanded={anchorEl ? 'true' : undefined}
      >
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
          {user ? getInitials(getUserDisplayName()) : <PersonIcon />}
        </Avatar>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        id='user-menu'
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            elevation: 3,
            sx: {
              minWidth: 200,
              mt: 1.5,
              borderRadius: 2,
              '& .MuiMenuItem-root': {
                borderRadius: 1,
                mx: 1,
                my: 0.5,
              },
            },
          },
        }}
      >
        {/* User Info Header */}
        <MenuItem disabled sx={{ py: 1.5 }}>
          <ListItemIcon>
            <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
              {user ? getInitials(getUserDisplayName()) : <PersonIcon />}
            </Avatar>
          </ListItemIcon>
          <ListItemText>
            <Typography variant='subtitle2' fontWeight={600}>
              {getUserDisplayName()}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {user?.email}
            </Typography>
            <Typography variant='caption' color='primary.main'>
              {user?.role}
            </Typography>
          </ListItemText>
        </MenuItem>

        <Divider sx={{ my: 1 }} />

        {/* Profile */}
        <MenuItem onClick={handleProfile}>
          <ListItemIcon>
            <AccountIcon fontSize='small' />
          </ListItemIcon>
          <ListItemText>Perfil</ListItemText>
        </MenuItem>

        {/* Logout */}
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <LogoutIcon fontSize='small' sx={{ color: 'error.main' }} />
          </ListItemIcon>
          <ListItemText>Cerrar Sesión</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};